import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { TypographyH5, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import type { useDocumentEmail } from './hooks/useDocumentEmail';

type EmailState = ReturnType<typeof useDocumentEmail>;

export function DocumentEmailDialog({
  emailState,
}: Readonly<{ emailState: EmailState }>) {
  const { t } = useTranslations();

  return (
    <Dialog open={emailState.isOpen} onOpenChange={emailState.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <TypographyH5>{t('sendAsEmailModal.title')}</TypographyH5>
          </DialogTitle>
          <TypographyP2 className="text-text-secondary">
            {t('sendAsEmailModal.documentBody')}
          </TypographyP2>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="document-email">{t('sendAsEmailModal.emailField')}</Label>
          <Input
            autoFocus
            type="email"
            id="document-email"
            placeholder={t('sendAsEmailModal.email')}
            value={emailState.email}
            onChange={(e) => emailState.setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter' && emailState.email && !emailState.isPending) {
                void emailState.handleSend();
              }
            }}
          />
        </div>
        <DialogFooter>
          <div className="flex w-full flex-row items-center justify-end gap-2 pt-4">
            <Button
              variant="ghost"
              size="md"
              onClick={() => emailState.onOpenChange(false)}
            >
              {t('sendAsEmailModal.cancel')}
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={() => void emailState.handleSend()}
              disabled={emailState.isPending || !emailState.email}
            >
              {t('sendAsEmailModal.sendEmail')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
